/**
 * Helpers for the optional pixel warm-up in the new campaign wizard.
 *
 * Wizard state used here (step content `warmup`):
 *   startChoice        'direct' (the default: the usual flow), 'warmup' or 'automatic'
 *   mode               'manual' or 'automated'
 *   targetEvent        wizard value of the event the campaign is after
 *   warmupEvent        wizard value of the event of the warm-up ad set (start choice 'warmup')
 *   warmupBudget       daily budget of the warm-up ad set as typed (major units)
 *   maxDailyBudget     maximum daily budget of the whole campaign as typed (major units)
 *
 * Without a choice other than 'direct' (and without a typed maximum) nothing is added to the launch request, so the
 * campaign is created the usual way.
 */
import { LEAD_EVENT_TYPES, SALES_EVENT_TYPES } from '@/utils/ad-campaign-constants'
import { parseBudgetAmount, toMinorUnits } from '@/utils/audience-builder'

export const WARMUP_STEP = 'warmup'
// Step content `{ feature: boolean }`: whether the budget step offered the warm-up features the last time it was shown.
// Only what is offered now may reach the launch request.
export const WARMUP_OFFERED_STEP = 'warmupOffered'

export const START_CHOICES = Object.freeze({ DIRECT: 'direct', WARMUP: 'warmup', AUTOMATIC: 'automatic' })
export const MODES = Object.freeze({ MANUAL: 'manual', AUTOMATED: 'automated' })
export const EVENT_CHOICE_AUTOMATIC = 'automatic'

export const START_CHOICE_LABELS = Object.freeze({
    [START_CHOICES.DIRECT]: 'No warm-up (current flow)',
    [START_CHOICES.WARMUP]: 'Start with a warm-up',
    [START_CHOICES.AUTOMATIC]: 'Automatic',
})

export const START_CHOICE_HINTS = Object.freeze({
    [START_CHOICES.DIRECT]: 'All audience ad sets are launched now, exactly as before.',
    [START_CHOICES.WARMUP]:
        'One broad warm-up ad set starts first on a more frequent event. Your audience ad sets stay paused until the warm-up has collected enough events.',
    [START_CHOICES.AUTOMATIC]:
        'Turba checks your pixel when the campaign is published and starts with a warm-up only if it helps. Needs automated mode.',
})

export const MODE_LABELS = Object.freeze({
    [MODES.MANUAL]: 'Manual: Turba proposes the expansion and you confirm it',
    [MODES.AUTOMATED]: 'Automated: Turba expands the campaign itself within your maximum',
})

// A warm-up ad set budget below this (minor unit) is refused by the server
export const MIN_WARMUP_BUDGET = 200

/** The choice the wizard starts with and returns to: no warm-up. */
export const defaultWarmupChoice = () => ({ startChoice: START_CHOICES.DIRECT, mode: MODES.MANUAL })

const TARGET_EVENTS = Object.freeze({ sales: ['PURCHASE'], leads: ['LEAD', 'COMPLETE_REGISTRATION'] })
// Warm-up events by objective, deepest first, as far as they are shallower than the target event
const SALES_FUNNEL = ['CONTENT_VIEW', 'ADD_TO_CART', 'INITIATED_CHECKOUT', 'PURCHASE']

const normalised = (objective) => String(objective || '').toLowerCase()

/** Whether the objective can have a warm-up at all. */
export const isWarmupObjective = (objective) => Object.keys(TARGET_EVENTS).includes(normalised(objective))

export const targetEventOptions = (objective) => TARGET_EVENTS[normalised(objective)] || []

export const defaultTargetEvent = (objective) => targetEventOptions(objective)[0] || null

/** Warm-up events that can be picked for a target event, deepest first (the server checks the pick again). */
export const warmupEventOptions = (objective, targetEvent) => {
    const kind = normalised(objective)
    if (kind === 'leads') return targetEventOptions(kind).includes(targetEvent) ? ['CONTENT_VIEW'] : []
    if (kind !== 'sales') return []
    const depth = SALES_FUNNEL.indexOf(targetEvent)
    return depth <= 0 ? [] : SALES_FUNNEL.slice(0, depth).reverse()
}

/** Readable name of a wizard event value. */
export const eventLabel = (value) =>
    [...SALES_EVENT_TYPES, ...LEAD_EVENT_TYPES].find((option) => option.value === value)?.label || value || ''

/**
 * A typed maximum daily budget (major units) in minor units, or null. One strict parser for the wizard and the panel: digits
 * with an optional decimal part, nothing else ("1,5", "100abc", "0x10", "1e3" are refused).
 */
export const maximumToMinor = (typed) => {
    const text = String(typed ?? '').trim()
    if (!/^\d+(\.\d+)?$/.test(text)) return null
    const minor = toMinorUnits(Number(text))
    return Number.isFinite(minor) && minor > 0 ? minor : null
}

const minorOf = (typed) => {
    const amount = parseBudgetAmount(typed)
    return amount === null ? null : toMinorUnits(amount)
}

/** Sum of audience budgets in minor units from a list of amounts as typed (major units). */
export const sumMinor = (amounts) => amounts.reduce((total, amount) => total + toMinorUnits(Number(amount) || 0), 0)

/**
 * The `warmup` input of the launch request, or null for the usual flow. The warm-up event is only sent for the
 * 'warmup' start choice; for 'automatic' Turba chooses it.
 */
export const warmupForSubmission = (choice, objective) => {
    if (!choice || !choice.startChoice || choice.startChoice === START_CHOICES.DIRECT) return null
    const automatic = choice.startChoice === START_CHOICES.AUTOMATIC
    return {
        start_choice: choice.startChoice,
        mode: automatic ? MODES.AUTOMATED : choice.mode || MODES.MANUAL,
        target_event: choice.targetEvent || defaultTargetEvent(objective),
        ...(automatic ? {} : { warmup_event: choice.warmupEvent }),
        warmup_budget: minorOf(choice.warmupBudget),
        // Only an automatic start can be drawn for the holdout, and the flag is sent only when the box was ticked
        ...(automatic && choice.holdoutOptOut === true ? { holdout_opt_out: true } : {}),
    }
}

/** The maximum daily budget in minor units, or null when none was typed. */
export const maxDailyBudgetForSubmission = (choice) => maximumToMinor(choice?.maxDailyBudget)

/**
 * Why the choice cannot be launched, or null. `audienceAmounts` are the audience daily budgets as typed (major units).
 * A typed maximum is also checked without a warm-up: it must cover the audiences.
 * @param {object} choice
 * @param {{ audienceAmounts?: number[], minDailyBudget?: number, objective?: string }} [context]
 * @returns {string|null}
 */
export const describeWarmupProblem = (choice, { audienceAmounts = [], minDailyBudget = 0, objective } = {}) => {
    const start = choice?.startChoice || START_CHOICES.DIRECT
    // A choice for an objective that cannot have a warm-up is never launched
    if (start !== START_CHOICES.DIRECT && !isWarmupObjective(objective)) {
        return 'A warm-up is only available for sales and lead campaigns.'
    }
    const typedMax = String(choice?.maxDailyBudget ?? '').trim()
    const audiencesMinor = sumMinor(audienceAmounts)

    if (start === START_CHOICES.DIRECT && typedMax === '') return null

    const maxMinor = maximumToMinor(choice?.maxDailyBudget)
    if (maxMinor === null) return 'Enter the maximum daily budget for the whole campaign.'

    if (start === START_CHOICES.DIRECT) {
        return maxMinor < audiencesMinor
            ? 'The maximum daily budget must cover the daily budgets of all audiences.'
            : null
    }

    if (start === START_CHOICES.WARMUP) {
        const options = warmupEventOptions(objective, choice.targetEvent || defaultTargetEvent(objective))
        if (!choice.warmupEvent || (options.length > 0 && !options.includes(choice.warmupEvent))) {
            return 'Choose the event of the warm-up ad set.'
        }
    }

    const warmupMinor = minorOf(choice.warmupBudget)
    const minimum = Math.max(MIN_WARMUP_BUDGET, Number(minDailyBudget) || 0)
    if (warmupMinor === null || warmupMinor < minimum) {
        return 'Enter a daily budget for the warm-up ad set that is at least the minimum of your ad account.'
    }
    if (maxMinor < audiencesMinor + warmupMinor) {
        return 'The maximum daily budget must cover the warm-up ad set and the daily budgets of all audiences.'
    }
    return null
}

// The maximum daily budget is stored in an integer column (minor units)
const MAX_STORED_MINOR = 2147483647

/**
 * Why a typed maximum daily budget (major units) cannot be saved, or null. `minimumMinor` is what the panel already
 * knows must fit under it (the warm-up ad set and the budgets of the audiences held back), 0 or null when unknown; the
 * server checks everything else and its refusal is shown as it is.
 */
export const describeMaximumProblem = (typed, { minimumMinor = 0 } = {}) => {
    const minor = maximumToMinor(typed)
    if (minor === null) {
        return 'Enter the maximum daily budget for the whole campaign as a number, for example 150 or 150.50.'
    }
    if (minor > MAX_STORED_MINOR) return 'This maximum daily budget is too large.'
    if (minimumMinor && minor < minimumMinor) {
        return 'The maximum daily budget must cover the warm-up ad set and the daily budgets of the audiences held back.'
    }
    return null
}

/** Server refusals of a launch that are about the warm-up or the maximum, in words for the wizard; else null. */
const LAUNCH_ERROR_MESSAGES = Object.freeze({
    INVALID_MAX_DAILY_BUDGET:
        'The maximum daily budget must cover the warm-up ad set and the daily budgets of all audiences.',
})

export const getWarmupLaunchErrorMessage = (error) => {
    const first = error?.graphQLErrors?.[0]
    const code = first?.extensions?.code
    if (
        [
            'PIXEL_WARMUP_NOT_AVAILABLE',
            'INVALID_WARMUP_INPUT',
            'MAX_DAILY_BUDGET_CURRENCY_NOT_SUPPORTED',
            'WARMUP_CURRENCY_NOT_SUPPORTED',
        ].includes(code)
    ) {
        return first.message
    }
    return LAUNCH_ERROR_MESSAGES[code] || null
}

/**
 * Whether the warm-up features (start choice, maximum daily budget, automatic event) may be sent with a launch now:
 * a new campaign for a sales or lead objective with audience budgets, for a licence whose budget step offered them.
 * A choice made while they were offered and hidden since (another objective, the feature switched off, a failed read)
 * is ignored, so it can neither block the launch nor change it.
 */
export const warmupFeaturesOffered = ({
    objective,
    offeredStep,
    isAddAudience = false,
    isEditMode = false,
    campaignBudget = false,
}) => !isAddAudience && !isEditMode && !campaignBudget && isWarmupObjective(objective) && offeredStep?.feature === true

/**
 * The event an audience launches with, the one rule behind the audience step, the review and the launch request.
 * "Automatic" counts only while the warm-up features are offered; a dropped pick launches the objective's default event
 * (the event the Automatic note named); an automatic audience keeps its stored event, which the server replaces.
 * @param {{ eventChoice: string|null, stored?: string|null, objective: string, offered: boolean, warmup?: object }} input
 * @returns {{ automatic: boolean, event: string|null, stored?: string|null }}
 */
export const resolveLaunchEvent = ({ eventChoice, stored = null, objective, offered, warmup = {} }) => {
    if (!eventChoice) return { automatic: false, event: stored }
    if (!offered) return { automatic: false, event: defaultTargetEvent(objective) || stored }
    const started = warmup?.startChoice && warmup.startChoice !== START_CHOICES.DIRECT && warmup.targetEvent
    return { automatic: true, event: started ? warmup.targetEvent : defaultTargetEvent(objective), stored }
}

/** The event choice an audience asked for: 'automatic' only after the user picked it, else null. */
export const eventChoiceForSubmission = (settings) =>
    settings?.event_choice === EVENT_CHOICE_AUTOMATIC ? EVENT_CHOICE_AUTOMATIC : null
